import type React from 'react';
import { useMemo } from 'react';
import { DeploymentSelector } from '../../../components/message-composer/chat-context/selectors';
import { useDeploymentOptions } from '../../../hooks/portal/useDeploymentOptions';
import { CUSTOMER_DIMENSION } from '../../types/controls';
import type { ControlComponentProps } from './types';

/** A product limit: each pick is another query and another series on every chart. */
const DASHBOARD_MAX_DEPLOYMENTS = 5;

/**
 * The cascade's second step, showing the chosen customer's fleet.
 *
 * Ignores the `options` the filter bar resolved for it: the backend never enumerates instances, so
 * that list is only the caller's already-verified scope. Rows come from the portal instead.
 */
export const DeploymentControl: React.FC<ControlComponentProps> = ({
  control,
  templateProducts,
  parentSelections,
  value,
  onChange,
  disabled,
}) => {
  const customers = parentSelections?.[CUSTOMER_DIMENSION];

  const { items, totalCount, status, nameOf } = useDeploymentOptions({
    products: templateProducts,
    customers,
  });

  const multiple = control.multiple ?? false;

  // Named from the directory the rows come from, so the chip reads as the list did.
  const selectedItems = useMemo(() => value.map((id) => ({ id, label: nameOf(id) })), [value, nameOf]);

  return (
    <DeploymentSelector
      items={items}
      selectedItems={selectedItems}
      onSelectionChange={(picked) => onChange(picked.map((item) => item.id))}
      multiple={multiple}
      maxSelection={DASHBOARD_MAX_DEPLOYMENTS}
      status={status}
      disabled={disabled}
      variant="field"
      fieldLabel={control.label}
      compact
      menuOpenBelow
      searchPlaceholder={`Search ${control.label.toLowerCase()}...`}
      totalCount={totalCount}
    />
  );
};
